/**
 * Hook to handle navigation events from the tray popover window
 * This allows the tray popover to navigate the main window to specific pages
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isTauri, isMainWindow } from '@/lib/utils/tauri';

interface TrayNavigatePayload {
  path: string;
}

export function useTrayNavigation() {
  const navigate = useNavigate();

  useEffect(() => {
    // Only listen in Tauri
    if (!isTauri()) {
      return;
    }

    let unlisten: (() => void) | undefined;
    let cancelled = false;

    const setupListener = async () => {
      try {
        // Check if this is the main window (async check)
        const isMain = await isMainWindow();
        if (!isMain || cancelled) {
          return;
        }

        const { listen } = await import('@tauri-apps/api/event');
        if (cancelled) return;

        unlisten = await listen<TrayNavigatePayload>('tray-navigate', (event) => {
          if (event.payload?.path) {
            navigate(event.payload.path);
          }
        });
      } catch (error) {
        console.error('Failed to setup tray navigation listener:', error);
      }
    };

    setupListener();

    return () => {
      cancelled = true;
      if (unlisten) {
        unlisten();
      }
    };
  }, [navigate]);
}
